import { AbsoluteFill, Video, staticFile } from "remotion";
import type { CalculateMetadataFunction } from "remotion";

// Reads public/hyperframes-in/manifest.json, written by Hermes after every
// HyperFrames render (ffprobe-verified width/height/fps/nbFrames per clip).
// Add a new clip by appending to the manifest and copying the MP4 into
// public/hyperframes-in/<project>/ — no code change needed here.

type ManifestClip = {
  id: string;
  path: string;
  width: number;
  height: number;
  fps: number;
  durationSec: number;
  nbFrames: number;
  source?: string;
  project?: string;
  composition?: string;
};

type Manifest = {
  clips: ManifestClip[];
};

export type HyperframesImportProps = {
  clipId: string;
  resolvedPath?: string;
};

export const hyperframesImportDefaultProps: HyperframesImportProps = {
  clipId: "akshar-intro",
};

const loadManifest = async (): Promise<Manifest> => {
  const response = await fetch(staticFile("hyperframes-in/manifest.json"));
  if (!response.ok) {
    throw new Error(
      `HyperframesImport: could not load manifest.json (HTTP ${response.status})`,
    );
  }
  return response.json();
};

const findClip = (manifest: Manifest, clipId: string): ManifestClip => {
  const clip = manifest.clips.find((c) => c.id === clipId);
  if (!clip) {
    const known = manifest.clips.map((c) => c.id).join(", ") || "(none)";
    throw new Error(
      `HyperframesImport: no manifest entry for clipId "${clipId}". Known ids: ${known}`,
    );
  }
  return clip;
};

// Resolves the clip's real width/height/fps/duration from the manifest so the
// Remotion timeline always matches the HyperFrames render exactly, and hands
// the resolved asset path down as a prop (avoids an async fetch inside the
// component's render path).
export const calculateHyperframesMetadata: CalculateMetadataFunction<
  HyperframesImportProps
> = async ({ props }) => {
  const manifest = await loadManifest();
  const clip = findClip(manifest, props.clipId);

  return {
    durationInFrames: clip.nbFrames,
    fps: clip.fps,
    width: clip.width,
    height: clip.height,
    props: {
      ...props,
      resolvedPath: clip.path,
    },
  };
};

export const HyperframesImport: React.FC<HyperframesImportProps> = ({
  resolvedPath,
}) => {
  if (!resolvedPath) {
    // calculateMetadata hasn't resolved yet (e.g. first static analysis pass).
    return <AbsoluteFill style={{ backgroundColor: "#000000" }} />;
  }

  return (
    <AbsoluteFill style={{ backgroundColor: "#000000" }}>
      <Video src={staticFile(resolvedPath)} />
    </AbsoluteFill>
  );
};
